import { useEffect, useState } from "react";
import { api } from "../../lib/api";

function ywiColor(v: number | null): string {
  if (v == null) return "#9aa";
  if (v >= 66) return "#2A9D8F";
  if (v >= 40) return "#F4A261";
  return "#E76F51";
}
function painColor(level: number): string {
  if (level <= 3) return "#2A9D8F";
  if (level <= 6) return "#F4A261";
  return "#E76F51";
}
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function PractitionerPortal() {
  const [members, setMembers] = useState<any[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const loadMembers = () => api.practitioner.members().then((r) => {
    setMembers(r.members);
    if (r.members.length && !selectedId) setSelectedId(r.members[0].id);
  }).catch((e) => setErr(e.message));
  const loadReferrals = () => api.practitioner.referrals().then((r) => setReferrals(r.referrals)).catch(() => {});

  useEffect(() => { loadMembers(); loadReferrals(); }, []);

  if (err) return <div className="p-4"><ErrorBox msg={err} /></div>;
  if (!members) return <div className="p-6"><Loading /></div>;

  const incomingPending = referrals.filter((r) => r.direction === "incoming" && r.status === "pending");

  return (
    <div className="h-full min-h-0 overflow-hidden bg-d_card/20 flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-d_border/20">
        <div className="text-[10px] tracking-[2px] uppercase text-d_accent font-bold mb-0.5">Practitioner</div>
        <h1 className="font-serif text-[24px] font-semibold text-d_text leading-tight">My Caseload</h1>
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* Left: caseload + incoming referrals */}
        <div className="w-[260px] border-r border-d_border/20 overflow-y-auto flex-shrink-0">
          {incomingPending.length > 0 && (
            <div className="p-2 border-b border-d_border/20 bg-d_coral/5">
              <div className="text-[9px] uppercase tracking-[1px] text-d_coral font-bold px-1 mb-1.5">
                {incomingPending.length} Referral{incomingPending.length > 1 ? "s" : ""} to you
              </div>
              {incomingPending.map((r) => (
                <ReferralInbox key={r.id} referral={r} onRespond={() => { loadReferrals(); loadMembers(); }} />
              ))}
            </div>
          )}

          <div className="p-2">
            <div className="text-[9px] uppercase tracking-[1px] text-d_text3 font-bold px-1 mb-1.5">
              {members.length} Member{members.length !== 1 ? "s" : ""}
            </div>
            {members.length === 0 ? (
              <p className="text-[12px] text-d_text3 px-1 py-4">No members assigned to you yet.</p>
            ) : members.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedId(m.id)}
                className={`w-full text-left px-2.5 py-2 rounded-lg mb-0.5 transition ${
                  selectedId === m.id ? "bg-d_accent/10 border border-d_accent/20" : "border border-transparent hover:bg-d_card2/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-d_text">{m.name}</span>
                  {m.pending_referrals > 0 && (
                    <span className="text-[9px] bg-d_coral text-white rounded-full w-4 h-4 flex items-center justify-center font-bold">{m.pending_referrals}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] uppercase tracking-[0.5px] text-d_text3">{m.tier}</span>
                  {m.ywi != null && (
                    <span className="text-[11px] font-serif font-bold" style={{ color: ywiColor(m.ywi) }}>YWI {m.ywi.toFixed(0)}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: selected member detail */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          {selectedId ? (
            <MemberDetail
              memberId={selectedId}
              referrals={referrals.filter((r) => r.member_id === selectedId)}
              onReferralChange={() => { loadReferrals(); loadMembers(); }}
            />
          ) : (
            <div className="p-8 text-center text-d_text3 text-[13px]">Select a member to see their context.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Member detail ──────────────────────────────────────────────────────────
function MemberDetail({ memberId, referrals, onReferralChange }: { memberId: string; referrals: any[]; onReferralChange: () => void }) {
  const [ctx, setCtx] = useState<any | null>(null);
  const [notes, setNotes] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadNotes = () => api.practitioner.notes(memberId).then((r) => setNotes(r.notes)).catch(() => setNotes([]));

  useEffect(() => {
    setCtx(null); setNotes(null); setErr(null);
    api.practitioner.member(memberId).then(setCtx).catch((e) => setErr(e.message));
    loadNotes();
  }, [memberId]);

  if (err) return <div className="p-4"><ErrorBox msg={err} /></div>;
  if (!ctx) return <div className="p-6"><Loading /></div>;

  const m = ctx.member;
  const y = ctx.ywi;
  const latestWear = ctx.wearable_recent?.[0];

  return (
    <div className="p-4 space-y-4">
      {/* Identity + YWI */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-[22px] font-semibold text-d_text leading-tight">{m.first_name} {m.last_name}</h2>
          <div className="text-[11px] text-d_text3 mt-0.5">{m.email} · <span className="uppercase tracking-[0.5px]">{m.tier}</span> · since {fmtDate(m.enrolled_at)}</div>
        </div>
        {y && (
          <div className="text-right">
            <div className="font-serif text-[30px] font-bold leading-none" style={{ color: ywiColor(Number(y.ywi_total)) }}>{Number(y.ywi_total).toFixed(0)}</div>
            <div className="text-[9px] uppercase tracking-[1px] text-d_text3">YWI · {y.classification}</div>
          </div>
        )}
      </div>

      {/* Dimensions */}
      {y && (
        <div className="grid grid-cols-5 gap-2">
          {[["Physical", y.physical, "#2A9D8F"], ["Emotional", y.emotional, "#E76F51"], ["Mental", y.mental, "#457B9D"], ["Spiritual", y.spiritual, "#F4A261"], ["Social", y.social, "#E9C46A"]].map(([label, val, color]: any) => (
            <div key={label} className="rounded-lg border border-d_border/20 p-2 text-center">
              <div className="text-[15px] font-serif font-bold" style={{ color }}>{Number(val).toFixed(1)}</div>
              <div className="text-[8px] uppercase tracking-[0.5px] text-d_text3 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Wearable snapshot */}
      <div className="rounded-lg border border-d_border/20 p-3">
        <div className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold mb-2">Recent Vitals {latestWear?.source ? `· ${latestWear.source}` : ""}</div>
        {latestWear ? (
          <div className="grid grid-cols-4 gap-2 text-center">
            <Vital label="HRV" value={latestWear.hrv_avg != null ? Math.round(latestWear.hrv_avg) + " ms" : "—"} />
            <Vital label="Resting HR" value={latestWear.resting_hr != null ? Math.round(latestWear.resting_hr) + " bpm" : "—"} />
            <Vital label="Sleep" value={latestWear.sleep_duration_hr != null ? latestWear.sleep_duration_hr.toFixed(1) + " h" : "—"} />
            <Vital label="Recovery" value={latestWear.recovery_score != null ? Math.round(latestWear.recovery_score) : "—"} />
          </div>
        ) : <p className="text-[12px] text-d_text3">No wearable data connected.</p>}
      </div>

      {/* Active pain */}
      {ctx.active_pain?.length > 0 && (
        <div className="rounded-lg border border-d_border/20 p-3">
          <div className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold mb-2">Active Pain ({ctx.active_pain.length})</div>
          <div className="space-y-1.5">
            {ctx.active_pain.map((p: any) => (
              <div key={p.id} className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-md flex items-center justify-center text-white text-[12px] font-bold flex-shrink-0" style={{ background: painColor(p.level) }}>{p.level}</span>
                <span className="text-[12px] text-d_text2 flex-1">{p.description || "Pain point"}</span>
                <span className="text-[10px] text-d_text3">{fmtDateTime(p.recorded_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent visits */}
      {ctx.recent_visits?.length > 0 && (
        <div className="rounded-lg border border-d_border/20 p-3">
          <div className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold mb-2">Recent Sessions</div>
          <div className="space-y-1">
            {ctx.recent_visits.slice(0, 5).map((v: any, i: number) => (
              <div key={i} className="flex items-center justify-between text-[12px]">
                <span className="text-d_text2">{v.session_name || v.session_category}</span>
                <span className="text-d_text3 text-[10px]">{fmtDate(v.visited_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Referrals for this member */}
      <ReferralSection memberId={memberId} referrals={referrals} onChange={onReferralChange} />

      {/* Notes */}
      <NotesSection memberId={memberId} notes={notes} onAdded={loadNotes} />
    </div>
  );
}

function Vital({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-[15px] font-serif font-semibold text-d_text">{value}</div>
      <div className="text-[8px] uppercase tracking-[0.5px] text-d_text3 mt-0.5">{label}</div>
    </div>
  );
}

// ─── Notes ──────────────────────────────────────────────────────────────────
function NotesSection({ memberId, notes, onAdded }: { memberId: string; notes: any[] | null; onAdded: () => void }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await api.practitioner.addNote(memberId, { text: text.trim() }); setText(""); onAdded(); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-d_border/20 p-3">
      <div className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold mb-2 flex items-center gap-1.5">
        Session Notes <span className="text-[8px] text-d_accent normal-case tracking-normal">🔒 encrypted</span>
      </div>

      <div className="mb-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a session note (encrypted at rest)…"
          rows={2}
          className="w-full text-[12px] bg-d_card2/40 border border-d_border/30 rounded-md px-2.5 py-2 text-d_text placeholder:text-d_text3 resize-none focus:outline-none focus:border-d_accent/50"
        />
        <div className="flex justify-end mt-1.5">
          <button onClick={save} disabled={saving || !text.trim()}
            className="text-[11px] font-semibold px-3 py-1 rounded-md bg-d_accent text-white hover:opacity-90 disabled:opacity-40 transition">
            {saving ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>

      {notes == null ? <Loading /> : notes.length === 0 ? (
        <p className="text-[12px] text-d_text3">No notes yet.</p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="rounded-md border border-d_border/15 bg-d_card2/30 p-2.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-d_text3">{fmtDateTime(n.session_date)}</span>
                {n.is_mine && <span className="text-[8px] uppercase tracking-[0.5px] text-d_accent font-bold">You</span>}
              </div>
              <p className="text-[12px] text-d_text2 whitespace-pre-wrap leading-relaxed">{n.text}</p>
              {n.tags?.length > 0 && (
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {n.tags.map((t: string) => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-d_accent/10 text-d_accent">{t}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Referrals ──────────────────────────────────────────────────────────────
function ReferralSection({ memberId, referrals, onChange }: { memberId: string; referrals: any[]; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [colleagues, setColleagues] = useState<any[]>([]);
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [modality, setModality] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open && colleagues.length === 0) api.practitioner.colleagues().then((r) => setColleagues(r.colleagues)); }, [open]);

  const submit = async () => {
    if (!to || !reason.trim()) return;
    setSaving(true);
    try {
      await api.practitioner.addReferral({ member_id: memberId, to_practitioner: to, reason: reason.trim(), modality_suggested: modality || undefined });
      setOpen(false); setTo(""); setReason(""); setModality(""); onChange();
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-d_border/20 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold">Cross-Referrals</span>
        <button onClick={() => setOpen(!open)} className="text-[11px] text-d_accent font-semibold hover:opacity-80">
          {open ? "Cancel" : "+ Refer"}
        </button>
      </div>

      {open && (
        <div className="mb-3 space-y-2 rounded-md border border-d_border/20 p-2.5 bg-d_card2/30">
          <select value={to} onChange={(e) => setTo(e.target.value)}
            className="w-full text-[12px] bg-d_card border border-d_border/30 rounded-md px-2 py-1.5 text-d_text focus:outline-none">
            <option value="">Refer to…</option>
            {colleagues.map((c) => <option key={c.auth_user_id} value={c.auth_user_id}>{c.name}</option>)}
          </select>
          <input value={modality} onChange={(e) => setModality(e.target.value)} placeholder="Suggested modality (e.g. counseling)"
            className="w-full text-[12px] bg-d_card border border-d_border/30 rounded-md px-2 py-1.5 text-d_text placeholder:text-d_text3 focus:outline-none" />
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for referral…" rows={2}
            className="w-full text-[12px] bg-d_card border border-d_border/30 rounded-md px-2 py-1.5 text-d_text placeholder:text-d_text3 resize-none focus:outline-none" />
          <button onClick={submit} disabled={saving || !to || !reason.trim()}
            className="w-full text-[11px] font-semibold py-1.5 rounded-md bg-d_accent text-white hover:opacity-90 disabled:opacity-40 transition">
            {saving ? "Sending…" : "Send referral"}
          </button>
        </div>
      )}

      {referrals.length === 0 ? (
        <p className="text-[12px] text-d_text3">No referrals for this member.</p>
      ) : (
        <div className="space-y-1.5">
          {referrals.map((r) => (
            <div key={r.id} className="text-[12px] rounded-md border border-d_border/15 bg-d_card2/30 p-2">
              <div className="flex items-center justify-between">
                <span className="text-d_text2">
                  {r.direction === "incoming" ? `From ${r.from_name}` : `To ${r.to_name}`}
                  {r.modality_suggested ? ` · ${r.modality_suggested}` : ""}
                </span>
                <StatusPill status={r.status} />
              </div>
              <p className="text-[11px] text-d_text3 mt-0.5">{r.reason}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralInbox({ referral, onRespond }: { referral: any; onRespond: () => void }) {
  const [busy, setBusy] = useState(false);
  const respond = async (status: "accepted" | "declined") => {
    setBusy(true);
    try { await api.practitioner.respondReferral(referral.id, status); onRespond(); }
    catch (e) { console.error(e); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-d_coral/20 bg-d_card p-2 mb-1.5">
      <div className="text-[11px] text-d_text2"><strong>{referral.member_name}</strong> from {referral.from_name}</div>
      <p className="text-[10px] text-d_text3 mt-0.5 mb-1.5">{referral.reason}</p>
      <div className="flex gap-1.5">
        <button onClick={() => respond("accepted")} disabled={busy} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-d_accent text-white hover:opacity-90 disabled:opacity-40">Accept</button>
        <button onClick={() => respond("declined")} disabled={busy} className="text-[10px] font-semibold px-2 py-0.5 rounded border border-d_border/30 text-d_text3 hover:text-d_text2">Decline</button>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = { pending: "#F4A261", accepted: "#2A9D8F", declined: "#E76F51", completed: "#457B9D" };
  return <span className="text-[9px] uppercase tracking-[0.5px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: (map[status] || "#888") + "22", color: map[status] || "#888" }}>{status}</span>;
}

function Loading() { return <div className="text-center py-8 text-d_text3 text-[12px] uppercase tracking-wider">Loading…</div>; }
function ErrorBox({ msg }: { msg: string }) { return <div className="rounded-lg border border-d_coral/30 bg-d_coral/5 p-4 text-[12px] text-d_coral">{msg}</div>; }
